import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  CalendarClock,
  CalendarPlus,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleDollarSign,
  ClipboardList,
  Filter,
  GripVertical,
  Search,
  ShieldAlert,
  UserRound,
  UsersRound,
  WalletCards,
  X,
} from "lucide-react";
import { toast } from "sonner";
import BookFutureNeedDialog from "@/components/checkout/BookFutureNeedDialog";
import { supabase } from "@/lib/supabaseClient";
import { formatDoctorName } from "@/lib/doctorName";
import { useBranch } from "@/context/BranchContext";

const STAGES = [
  { key: "active", label: "Active", hint: "Accepted cases move here after bonding starts.", wide: true },
  { key: "consultation", label: "Consultation", hint: "Cases arrive here when the patient is screened." },
  { key: "records_taken", label: "Records Taken", hint: "Cases arrive here when records are uploaded." },
  { key: "case_proposed", label: "Case Proposed", hint: "Cases arrive here when the ortho plan is presented." },
  { key: "retention", label: "Retention", hint: "Debanded cases waiting through retainer workflow." },
  { key: "completed", label: "Completed", hint: "Closed cases live in the archive.", collapsedDefault: true },
];

const JOURNEY_STAGES = ["consultation", "records_taken", "case_proposed", "active", "retention", "completed"];

const EMPTY_FILTERS = {
  doctorId: "",
  status: "",
  payment: "",
  search: "",
};

const TABS = [
  { key: "attention", label: "Needs attention", Icon: CalendarClock },
  { key: "records", label: "Needs records", Icon: ClipboardList },
  { key: "retainers", label: "Retainers", Icon: ShieldAlert },
  { key: "all", label: "All patients", Icon: UsersRound },
  { key: "pipeline", label: "Pipeline", Icon: GripVertical, secondary: true },
];

const ORTHO_CENTER_TUTORIAL_CASES = [
  {
    ortho_case_id: "tutorial-ortho-records",
    patient_id: "tutorial-ortho-patient-records",
    patient_name: "Ana Reyes",
    patient_number: "ORTHO-DEMO-101",
    doctor_id: "tutorial-doctor-mara",
    doctor_name: "Dr. Mara Santos",
    branch_name: "Main Branch",
    pipeline_stage: "consultation",
    status: "active",
    case_age_days: 4,
    collection_status: "not_started",
    case_value_centavos: 0,
    total_paid_centavos: 0,
    contract_remaining_centavos: 0,
    ar_outstanding_centavos: 0,
    tutorial_target_id: "ortho-demo-records-row",
  },
  {
    ortho_case_id: "tutorial-ortho-adjustment",
    patient_id: "tutorial-ortho-patient-adjustment",
    patient_name: "Marco Lim",
    patient_number: "ORTHO-DEMO-102",
    doctor_id: "tutorial-doctor-mara",
    doctor_name: "Dr. Mara Santos",
    branch_name: "Main Branch",
    pipeline_stage: "active",
    status: "active",
    is_overdue: true,
    needs_next_booking: true,
    days_since_last_adjustment: 49,
    next_adjustment_due_at: "2026-07-10T09:00:00.000Z",
    adjustments_done_count: 6,
    adjustments_total_count: 18,
    collection_status: "current",
    case_value_centavos: 8500000,
    total_paid_centavos: 3500000,
    contract_remaining_centavos: 5000000,
    ar_outstanding_centavos: 0,
  },
  {
    ortho_case_id: "tutorial-ortho-retainer",
    patient_id: "tutorial-ortho-patient-retainer",
    patient_name: "Bea Cruz",
    patient_number: "ORTHO-DEMO-103",
    doctor_id: "tutorial-doctor-levi",
    doctor_name: "Dr. Levi Jordan",
    branch_name: "Main Branch",
    pipeline_stage: "retention",
    status: "retention",
    retention_substage: "delivery_due",
    collection_status: "settled",
    case_value_centavos: 7000000,
    total_paid_centavos: 7000000,
    contract_remaining_centavos: 0,
    ar_outstanding_centavos: 0,
  },
];

function orthoCenterTutorialDashboard() {
  const pipeline = Object.fromEntries(STAGES.map((stage) => {
    const cases = ORTHO_CENTER_TUTORIAL_CASES.filter((row) => row.pipeline_stage === stage.key);
    return [stage.key, {
      count: cases.length,
      value_centavos: cases.reduce((sum, row) => sum + Number(row.case_value_centavos || 0), 0),
      contract_remaining_centavos: cases.reduce((sum, row) => sum + Number(row.contract_remaining_centavos || 0), 0),
      ar_outstanding_centavos: cases.reduce((sum, row) => sum + Number(row.ar_outstanding_centavos || 0), 0),
      cases,
    }];
  }));
  return {
    kpis: { open_cases_count: 3, unpaid_cases_count: 0, needs_follow_up_count: 3 },
    pipeline,
    at_risk_queue: [],
    retainer_pipeline: [],
    filter_options: {
      doctors: [
        { id: "tutorial-doctor-mara", label: "Dr. Mara Santos" },
        { id: "tutorial-doctor-levi", label: "Dr. Levi Jordan" },
      ],
      statuses: ["consultation", "active", "retention"],
      payments: ["not_started", "current", "settled"],
    },
    last_refreshed_at: new Date().toISOString(),
  };
}

export default function OrthoCenter() {
  const navigate = useNavigate();
  const { activeBranchId, loading: branchLoading } = useBranch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [movingCaseId, setMovingCaseId] = useState("");
  const [expandedStages, setExpandedStages] = useState(() => new Set(["active", "consultation", "retention"]));
  const [selectedTab, setSelectedTab] = useState("attention");
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [selectedPipelineStage, setSelectedPipelineStage] = useState("active");
  const [pipelineBoardOpen, setPipelineBoardOpen] = useState(false);
  const [bookingCase, setBookingCase] = useState(null);
  const tutorialDemoMode = searchParams.get("tutorial") === "ortho-module" && searchParams.get("demo") === "1";

  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (tutorialDemoMode) {
      setDashboard(normalizeDashboard(orthoCenterTutorialDashboard()));
      setLoading(false);
      return;
    }
    if (branchLoading || !activeBranchId) {
      return;
    }
    const payload = {
      p_branch_id: activeBranchId,
      p_doctor_id: filters.doctorId && filters.doctorId !== "__unassigned__" ? filters.doctorId : null,
      p_status: filters.status || null,
      p_payment: filters.payment || null,
    };
    const { data, error: err } = await supabase.rpc("get_ortho_center_dashboard", payload);
    if (err) {
      setError(err.message || "Could not load Ortho Center.");
      setLoading(false);
      return;
    }
    setDashboard(normalizeDashboard(data));
    setLoading(false);
  }, [activeBranchId, branchLoading, filters.doctorId, filters.payment, filters.status, tutorialDemoMode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tutorialDemoMode) return undefined;
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load, tutorialDemoMode]);

  const filteredPipeline = useMemo(() => filterDashboardSearch(dashboard, filters), [dashboard, filters]);
  const visibleTotal = useMemo(() => {
    return STAGES.reduce((sum, stage) => sum + Number(filteredPipeline?.pipeline?.[stage.key]?.cases?.length || 0), 0);
  }, [filteredPipeline]);
  const totalCases = useMemo(() => {
    return STAGES.reduce((sum, stage) => sum + Number(dashboard?.pipeline?.[stage.key]?.count || 0), 0);
  }, [dashboard]);
  const activeFilterCount = countActiveFilters(filters);
  const allCases = useMemo(() => getAllCases(filteredPipeline), [filteredPipeline]);
  const workQueue = useMemo(() => buildWorkQueue(allCases, selectedTab), [allCases, selectedTab]);
  const selectedCase = useMemo(() => {
    if (workQueue.length === 0) return null;
    return workQueue.find((row) => row.ortho_case_id === selectedCaseId) || workQueue[0];
  }, [selectedCaseId, workQueue]);

  useEffect(() => {
    if (workQueue.length === 0) {
      if (selectedCaseId) setSelectedCaseId("");
      return;
    }
    if (!selectedCaseId || !workQueue.some((row) => row.ortho_case_id === selectedCaseId)) {
      setSelectedCaseId(workQueue[0].ortho_case_id);
    }
  }, [selectedCaseId, workQueue]);

  function setFilterPatch(patch) {
    const next = { ...filters, ...patch };
    const params = new URLSearchParams();
    if (tutorialDemoMode) {
      params.set("tutorial", "ortho-module");
      params.set("demo", "1");
    }
    if (next.doctorId) params.set("doctor_id", next.doctorId);
    if (next.status) params.set("status", next.status);
    if (next.payment) params.set("payment", next.payment);
    if (next.search) params.set("q", next.search);
    setSearchParams(params, { replace: true });
  }

  function toggleStage(stage) {
    setExpandedStages((current) => {
      const next = new Set(current);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  }

  async function transitionCase(caseId, toStage) {
    if (!caseId || !toStage || movingCaseId) return;
    setMovingCaseId(caseId);
    const { error: err } = await supabase.rpc("transition_ortho_case_stage", {
      p_case_id: caseId,
      p_to_stage: toStage,
      p_to_retention_substage: toStage === "retention" ? "impression_sent" : null,
      p_reason: "Moved from Ortho Center",
    });
    if (err) {
      toast.error(err.message || "Could not move ortho case.");
      setMovingCaseId("");
      return;
    }
    await load();
    setMovingCaseId("");
  }

  async function handleAdjustmentBooked() {
    toast.success("Ortho adjustment booked.");
    setBookingCase(null);
    await load();
  }

  return (
    <div data-testid="ortho-center" data-tutorial-id="ortho-center" className="space-y-5">
      <header className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold tracking-tight text-slate-950">Ortho Center</h1>
          <p className="mt-1 text-[14px] text-slate-500">Keep every case moving.</p>
        </div>
        <div className="shrink-0 text-[12px] text-slate-500">{lastUpdatedLabel(filteredPipeline?.last_refreshed_at)}</div>
      </header>

      {error ? (
        <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
          <AlertTriangle className="h-4 w-4" strokeWidth={2} />
          Couldn’t load Ortho Center: {error}
        </div>
      ) : null}

      <OrthoTodaySummary kpis={filteredPipeline?.kpis} loading={loading} />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <OrthoTabs selected={selectedTab} onSelect={setSelectedTab} counts={tabCounts(allCases)} />
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="ortho-filter-toggle"
            data-tutorial-id="ortho-filter-toggle"
            type="button"
            onClick={() => setFiltersOpen((value) => !value)}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-700 transition hover:border-teal-300 hover:text-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
          >
            <Filter className="h-4 w-4" strokeWidth={2.25} />
            Filters{activeFilterCount ? ` · ${activeFilterCount}` : ""}
          </button>
          <div className="text-[12px] text-slate-500">{visibleTotal.toLocaleString()} of {totalCases.toLocaleString()} patients</div>
          {activeFilterCount ? (
            <button type="button" onClick={() => setSearchParams(new URLSearchParams(), { replace: true })} className="inline-flex min-h-11 items-center gap-1 rounded-md px-2 text-[12px] text-slate-500 hover:bg-slate-50 hover:text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600">
              <X className="h-3.5 w-3.5" strokeWidth={2.25} />
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {filtersOpen ? (
        <div className="rounded-xl border border-slate-200 bg-white">
          <FilterPanel
            filters={filters}
            options={filteredPipeline?.filter_options}
            onChange={setFilterPatch}
          />
        </div>
      ) : null}

      {loading ? <LoadingShell /> : (
        selectedTab === "pipeline" ? (
          <ClinicalPipelineConsole
            pipeline={filteredPipeline?.pipeline}
            selectedStage={selectedPipelineStage}
            onSelectStage={setSelectedPipelineStage}
            selectedCaseId={selectedCaseId}
            onSelectCase={setSelectedCaseId}
            selectedCase={selectedCase}
            movingCaseId={movingCaseId}
            boardOpen={pipelineBoardOpen}
            onToggleBoard={() => setPipelineBoardOpen((value) => !value)}
            expandedStages={expandedStages}
            onToggleStage={toggleStage}
            onTransition={transitionCase}
            onBookAdjustment={setBookingCase}
            navigate={navigate}
          />
        ) : (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            <WorkQueue
              rows={workQueue}
              selectedCaseId={selectedCase?.ortho_case_id || ""}
              onSelect={setSelectedCaseId}
              onBookAdjustment={setBookingCase}
              tab={selectedTab}
            />
            <CaseSidecar
              row={selectedCase}
              moving={movingCaseId === selectedCase?.ortho_case_id}
              onOpen={() => selectedCase?.patient_id && navigate(`/patients/${selectedCase.patient_id}?tab=ortho`)}
              onOpenAr={() => selectedCase?.patient_id && navigate(`/ar-center?patient_id=${selectedCase.patient_id}`)}
              onMove={(stage) => transitionCase(selectedCase?.ortho_case_id, stage)}
              onBookAdjustment={() => selectedCase && setBookingCase(selectedCase)}
            />
          </div>
        )
      )}

      <BookFutureNeedDialog
        open={!!bookingCase}
        onOpenChange={(open) => {
          if (!open) setBookingCase(null);
        }}
        bundle={orthoAdjustmentBookingBundle(bookingCase)}
        patientId={bookingCase?.patient_id || ""}
        patientName={bookingCase?.patient_name || ""}
        defaultDoctorId={bookingCase?.doctor_id || ""}
        defaultChairId={bookingCase?.chair_id || ""}
        onBooked={handleAdjustmentBooked}
        appointmentStatus="Scheduled"
        priorVisitDate={orthoLastVisitDate(bookingCase)}
        doctorRequestedDays={orthoRequestedDays(bookingCase)}
        headingOverride="Book Ortho adjustment"
        subtitleOverride={orthoBookingSubtitle(bookingCase)}
        submitLabel="Book adjustment"
        appointmentSummary={bookingCase ? <OrthoBookingSummary row={bookingCase} /> : null}
        appointmentInputExtra={bookingCase ? {
          ortho_case_id: bookingCase.ortho_case_id,
          ortho_visit_type: "adjustment",
          notes: orthoBookingNotes(bookingCase),
        } : null}
        initialNotes={orthoBookingNotes(bookingCase)}
      />
    </div>
  );
}

function OrthoTodaySummary({ kpis, loading }) {
  const items = [
    {
      label: "Open cases",
      value: kpis?.open_cases_count ?? kpis?.active_cases_count ?? 0,
      Icon: UsersRound,
    },
    {
      label: "Unpaid visits",
      value: kpis?.unpaid_cases_count ?? 0,
      Icon: CircleDollarSign,
    },
    {
      label: "Need follow-up",
      value: kpis?.needs_follow_up_count ?? 0,
      Icon: CalendarClock,
    },
  ];
  return (
    <section
      data-testid="ortho-center-kpis"
      data-tutorial-id="ortho-center-kpis"
      aria-busy={loading}
      className="overflow-hidden rounded-xl bg-teal-700 text-teal-50 shadow-sm shadow-teal-950/10"
    >
      <div className="px-5 pt-4 text-[11px] font-bold uppercase tracking-[0.14em] text-teal-100">Ortho today</div>
      <div className="grid sm:grid-cols-3">
        {items.map(({ label, value, Icon }, index) => (
          <div
            key={label}
            className={[
              "flex min-h-[92px] items-center gap-3 px-5 py-4",
              index ? "border-t border-teal-500/50 sm:border-l sm:border-t-0" : "",
              loading ? "opacity-60" : "",
            ].join(" ")}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-600 text-teal-50">
              <Icon className="h-5 w-5" strokeWidth={2} />
            </span>
            <span>
              <span className="block text-[24px] font-bold leading-none tabular-nums text-white">{Number(value || 0).toLocaleString()}</span>
              <span className="mt-1.5 block text-[13px] font-medium text-teal-50">{label}</span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function OrthoTabs({ selected, onSelect, counts }) {
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2" data-tutorial-id="ortho-tabs">
      {TABS.map((tab) => {
        const Icon = tab.Icon;
        return (
          <button
            key={tab.key}
            type="button"
            data-testid={`ortho-tab-${tab.key}`}
            data-tutorial-id={`ortho-tab-${tab.key}`}
            aria-pressed={selected === tab.key}
            onClick={() => onSelect(tab.key)}
            className={[
              "inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-[13px] font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600",
              tab.secondary ? "lg:ml-auto" : "",
              selected === tab.key
                ? "border-teal-600 bg-teal-50 text-teal-800"
                : "border-slate-200 bg-white text-slate-600 hover:border-teal-200 hover:text-teal-800",
            ].join(" ")}
          >
            <Icon className="h-4 w-4" strokeWidth={2} />
            {tab.label}
            <span className={[
              "min-w-5 rounded-md px-1.5 py-0.5 text-center text-[11px] tabular-nums",
              selected === tab.key ? "bg-white text-teal-700" : "bg-slate-100 text-slate-500",
            ].join(" ")}>
              {counts[tab.key] || 0}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function WorkQueue({ rows, selectedCaseId, onSelect, onBookAdjustment, tab }) {
  return (
    <section data-testid="ortho-work-queue" data-tutorial-id="ortho-work-queue" className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div>
          <h2 className="text-[18px] font-bold text-slate-950">{queueTitle(tab)}</h2>
          <p className="mt-1 text-[13px] text-slate-500">{queueHint(tab)}</p>
        </div>
        <span className="text-[12px] font-medium text-slate-500">{rows.length} {rows.length === 1 ? "patient" : "patients"}</span>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-16 text-center">
          <CircleCheck className="mx-auto h-7 w-7 text-emerald-600" strokeWidth={2} />
          <div className="mt-3 text-[14px] font-bold text-slate-800">Nothing needs attention here</div>
          <div className="mt-1 text-[13px] text-slate-500">Choose another view to see more ortho patients.</div>
        </div>
      ) : (
        <div>
          <div className="hidden grid-cols-[minmax(150px,1.2fr)_minmax(120px,0.8fr)_minmax(140px,1fr)_minmax(140px,0.85fr)_20px] gap-4 border-b border-slate-100 bg-slate-50/80 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500 md:grid">
            <span>Patient</span>
            <span>Treatment</span>
            <span>Next step</span>
            <span>Payment</span>
            <span aria-hidden="true" />
          </div>
          <div className="divide-y divide-slate-100">
          {rows.map((row) => {
            const issue = tab === "financial" ? "Actual AR" : caseIssue(row);
            const action = tab === "financial" ? "Open in AR" : caseAction(row);
            const selected = row.ortho_case_id === selectedCaseId;
            const bookable = tab !== "financial" && canBookAdjustment(row);
            return (
              <div
                key={row.ortho_case_id}
                role="button"
                tabIndex={0}
                data-testid="ortho-work-row"
                data-tutorial-id={row.tutorial_target_id}
                onClick={() => onSelect(row.ortho_case_id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(row.ortho_case_id);
                  }
                }}
                className={[
                  "grid w-full cursor-pointer gap-4 px-5 py-4 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-teal-600 md:grid-cols-[minmax(150px,1.2fr)_minmax(120px,0.8fr)_minmax(140px,1fr)_minmax(140px,0.85fr)_20px] md:items-center",
                  selected ? "bg-teal-50/70 ring-1 ring-inset ring-teal-200" : "hover:bg-slate-50/80",
                ].join(" ")}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-700 text-[12px] font-bold text-white">
                    {patientInitials(row.patient_name)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-bold text-slate-950">{row.patient_name || "Unnamed patient"}</span>
                    <span className="mt-0.5 block truncate text-[11px] text-slate-500">{cleanDoctor(row.doctor_name)} · {row.branch_name || "Unassigned"}</span>
                  </span>
                </span>
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-slate-800">{stageLabel(row.pipeline_stage)}</span>
                  <span className="mt-1 block truncate text-[12px] text-slate-500">{treatmentProgressLabel(row)}</span>
                </span>
                <span className="min-w-0">
                  <span className={`inline-flex items-center rounded-md px-2 py-1 text-[11px] font-bold ${issueTone(issue)}`}>{issue}</span>
                  {bookable ? (
                    <button
                      type="button"
                      data-testid={`ortho-book-adjustment-${row.ortho_case_id}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect(row.ortho_case_id);
                        onBookAdjustment?.(row);
                      }}
                      className="mt-2 flex min-h-11 items-center gap-2 rounded-lg bg-teal-700 px-3 text-[12px] font-bold text-white transition hover:bg-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                    >
                      <CalendarPlus className="h-4 w-4" strokeWidth={2.25} />
                      Book next adjustment
                    </button>
                  ) : (
                    <span className="mt-1.5 block text-[13px] font-semibold text-slate-800">{action}</span>
                  )}
                </span>
                <FinancialStatus row={row} />
                <ChevronRight className="hidden h-4 w-4 text-slate-300 md:block" strokeWidth={2} />
              </div>
            );
          })}
          </div>
        </div>
      )}
    </section>
  );
}

function FinancialStatus({ row, compact = false }) {
  const status = row.collection_status || "not_started";
  const arOutstanding = Number(row.ar_outstanding_centavos || 0);
  const contractRemaining = Number(row.contract_remaining_centavos ?? row.open_balance_centavos ?? 0);
  const statusContent = {
    unpaid: { label: "Unpaid visit", tone: "text-rose-700", Icon: AlertTriangle },
    settled: { label: "Fully paid", tone: "text-emerald-700", Icon: CircleCheck },
    credit: { label: "Credit balance", tone: "text-sky-700", Icon: WalletCards },
    current: { label: "On track", tone: "text-teal-700", Icon: CircleCheck },
    not_started: { label: "No fees recorded", tone: "text-slate-500", Icon: CircleDollarSign },
  }[status] || { label: titleize(status), tone: "text-slate-600", Icon: CircleDollarSign };
  const Icon = statusContent.Icon;
  return (
    <span className="min-w-0">
      <span className={`flex items-center gap-1.5 text-[13px] font-bold ${statusContent.tone}`}>
        <Icon className="h-4 w-4 shrink-0" strokeWidth={2.25} />
        {statusContent.label}
      </span>
      {!compact ? (
        <span className="mt-1 block text-[11px] leading-4 text-slate-500">
          {arOutstanding > 0
            ? `${formatPeso(arOutstanding)} unpaid from past visits`
            : status === "not_started"
              ? "No payment activity"
              : contractRemaining > 0
                ? `${formatPeso(contractRemaining)} left in plan · ₱0 unpaid`
                : "₱0 unpaid"}
        </span>
      ) : null}
    </span>
  );
}

function ClinicalPipelineConsole({
  pipeline,
  selectedStage,
  onSelectStage,
  selectedCaseId,
  onSelectCase,
  movingCaseId,
  boardOpen,
  onToggleBoard,
  expandedStages,
  onToggleStage,
  onTransition,
  onBookAdjustment,
  navigate,
}) {
  const orderedStages = JOURNEY_STAGES.map((key) => STAGES.find((stage) => stage.key === key)).filter(Boolean);
  const selectedBucket = pipeline?.[selectedStage] || { count: 0, value_centavos: 0, contract_remaining_centavos: 0, ar_outstanding_centavos: 0, cases: [] };
  const stageRows = selectedBucket.cases || [];
  const currentCase = stageRows.find((row) => row.ortho_case_id === selectedCaseId) || stageRows[0] || null;
  return (
    <section className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <h2 className="text-[16px] font-bold text-slate-950">Clinical Pipeline</h2>
            <p className="mt-0.5 text-[12px] text-slate-500">Follow the case journey without letting empty stages take over the screen.</p>
          </div>
          <button
            type="button"
            data-testid="ortho-board-view-toggle"
            onClick={onToggleBoard}
            className={[
              "inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-[12px] font-bold transition",
              boardOpen ? "border-teal-300 bg-teal-50 text-teal-700" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
            ].join(" ")}
          >
            <GripVertical className="h-3.5 w-3.5" strokeWidth={2.25} />
            Board view
          </button>
        </div>
        <div data-testid="ortho-stage-rail" className="grid gap-2 p-3 md:grid-cols-3 xl:grid-cols-6">
          {orderedStages.map((stage) => {
            const bucket = pipeline?.[stage.key] || { count: 0, value_centavos: 0, contract_remaining_centavos: 0, ar_outstanding_centavos: 0, cases: [] };
            const active = selectedStage === stage.key;
            return (
              <button
                key={stage.key}
                type="button"
                onClick={() => onSelectStage(stage.key)}
                className={[
                  "rounded-md border px-3 py-2 text-left transition",
                  active ? "border-teal-300 bg-teal-50 ring-1 ring-teal-200" : "border-slate-200 bg-slate-50 hover:bg-white",
                  Number(bucket.count || 0) === 0 ? "opacity-75" : "",
                ].join(" ")}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-[13px] font-bold text-slate-900">{stage.label}</span>
                  <span className={[
                    "rounded px-1.5 py-0.5 text-[12px] font-bold tabular-nums",
                    active ? "bg-white text-teal-700 ring-1 ring-teal-100" : "bg-white text-slate-600 ring-1 ring-slate-200",
                  ].join(" ")}>
                    {bucket.count || 0}
                  </span>
                </span>
                <span className="mt-1 block text-[11px] text-slate-500">
                  {formatPeso(bucket.contract_remaining_centavos)} left in plans
                  {Number(bucket.ar_outstanding_centavos || 0) > 0 ? ` · ${formatPeso(bucket.ar_outstanding_centavos)} unpaid` : ""}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {boardOpen ? (
        <PipelineBoard
          pipeline={pipeline}
          expandedStages={expandedStages}
          movingCaseId={movingCaseId}
          onToggleStage={onToggleStage}
          onTransition={onTransition}
          navigate={navigate}
        />
      ) : (
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_340px]">
          <PipelineStageList
            stage={STAGES.find((item) => item.key === selectedStage)}
            bucket={selectedBucket}
            selectedCaseId={currentCase?.ortho_case_id || ""}
            onSelect={onSelectCase}
          />
          <CaseSidecar
            row={currentCase}
            moving={movingCaseId === currentCase?.ortho_case_id}
            onOpen={() => currentCase?.patient_id && navigate(`/patients/${currentCase.patient_id}?tab=ortho`)}
            onOpenAr={() => currentCase?.patient_id && navigate(`/ar-center?patient_id=${currentCase.patient_id}`)}
            onMove={(stage) => onTransition(currentCase?.ortho_case_id, stage)}
            onBookAdjustment={() => currentCase && onBookAdjustment?.(currentCase)}
          />
        </div>
      )}
    </section>
  );
}

function PipelineStageList({ stage, bucket, selectedCaseId, onSelect }) {
  const rows = bucket?.cases || [];
  return (
    <section className="min-w-0 rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <h3 className="text-[16px] font-bold text-slate-950">{stage?.label || "Stage"}</h3>
          <p className="mt-0.5 text-[12px] text-slate-500">
            {rows.length} cases · {formatPeso(bucket?.contract_remaining_centavos)} left in plans
            {Number(bucket?.ar_outstanding_centavos || 0) > 0 ? ` · ${formatPeso(bucket?.ar_outstanding_centavos)} unpaid` : ""}
          </p>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-12 text-center">
          <div className="text-[14px] font-bold text-slate-700">No cases here</div>
          <div className="mt-1 text-[12px] text-slate-500">{stage?.hint || "Nothing needs action in this stage."}</div>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((row) => {
            const issue = caseIssue(row);
            const selected = selectedCaseId === row.ortho_case_id;
            return (
              <button
                key={row.ortho_case_id}
                type="button"
                data-testid="ortho-pipeline-row"
                onClick={() => onSelect(row.ortho_case_id)}
                className={[
                  "grid w-full gap-3 px-4 py-3 text-left transition md:grid-cols-[minmax(180px,1.2fr)_minmax(170px,0.9fr)_minmax(120px,0.6fr)]",
                  selected ? "bg-teal-50/70 ring-1 ring-inset ring-teal-200" : "hover:bg-slate-50",
                ].join(" ")}
              >
                <span className="min-w-0">
                  <span className="block truncate text-[14px] font-bold text-slate-950">{row.patient_name || "Unnamed patient"}</span>
                  <span className="mt-0.5 block truncate text-[11px] text-slate-500">{cleanDoctor(row.doctor_name)} · {row.branch_name || "Unassigned"}</span>
                </span>
                <span>
                  <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-bold ${issueTone(issue)}`}>{issue}</span>
                  <span className="mt-1 block text-[12px] text-slate-500">{caseSubline(row)}</span>
                </span>
                <FinancialStatus row={row} />
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

function CaseSidecar({ row, moving, onOpen, onOpenAr, onMove, onBookAdjustment }) {
  if (!row) {
    return (
      <aside data-testid="ortho-case-sidecar" data-tutorial-id="ortho-case-sidecar" className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center text-[13px] font-medium text-slate-500">
        Select a patient to see the next clinic step.
      </aside>
    );
  }
  const bookable = canBookAdjustment(row);
  const hasAr = Number(row.ar_outstanding_centavos || 0) > 0;
  const completed = Number(row.adjustments_done_count || 0);
  const total = Number(row.adjustments_total_count || 0);
  const progress = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const primaryLabel = bookable ? "Book next adjustment" : hasAr ? "Open AR" : "Open patient";
  const primaryAction = bookable ? onBookAdjustment : hasAr ? onOpenAr : onOpen;
  return (
    <aside data-testid="ortho-case-sidecar" data-tutorial-id="ortho-case-sidecar" className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-teal-700 text-[13px] font-bold text-white">
            {patientInitials(row.patient_name)}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[18px] font-bold text-slate-950">{row.patient_name || "Unnamed patient"}</h2>
            <p className="mt-0.5 truncate text-[12px] text-slate-500">{cleanDoctor(row.doctor_name)} · {row.branch_name || "Unassigned"}</p>
            <span className="mt-2 inline-flex rounded-md bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-700">{stageLabel(row.pipeline_stage)}</span>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Next step</div>
          <div className="mt-1.5 text-[20px] font-bold leading-6 text-slate-950">{caseAction(row)}</div>
          <div className="mt-1 text-[12px] text-slate-500">{caseIssue(row)}</div>
          <button
            type="button"
            data-testid={bookable ? "ortho-sidecar-book-adjustment" : hasAr ? "ortho-sidecar-open-ar" : "ortho-sidecar-primary"}
            onClick={primaryAction}
            className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 text-[13px] font-bold text-white transition hover:bg-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
          >
            {bookable ? <CalendarPlus className="h-4 w-4" strokeWidth={2.25} /> : hasAr ? <CircleDollarSign className="h-4 w-4" strokeWidth={2.25} /> : <UserRound className="h-4 w-4" strokeWidth={2.25} />}
            {primaryLabel}
          </button>
        </div>
      </div>

      <section className="border-t border-slate-100 px-5 py-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Treatment progress</h3>
          <span className="text-[13px] font-bold tabular-nums text-slate-800">{completed} of {total || "Not set"}</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-label="Treatment progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow={progress}>
          <div className="h-full rounded-full bg-teal-600" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 text-[12px]">
          <MetricRow label="Last visit" value={lastVisitLabel(row)} stacked />
          <MetricRow label="Next due" value={nextDueLabel(row)} danger={row.is_overdue} stacked />
        </div>
      </section>

      <section className="border-t border-slate-100 px-5 py-5">
        <h3 className="text-[12px] font-bold uppercase tracking-[0.08em] text-slate-500">Payment summary</h3>
        <div className="mt-3 space-y-2.5">
          <MetricRow label="Paid" value={formatPeso(row.total_paid_centavos)} />
          <MetricRow label="Left in plan" value={formatPeso(row.contract_remaining_centavos ?? row.open_balance_centavos)} />
          <MetricRow label="Unpaid from past visits" value={formatPeso(row.ar_outstanding_centavos)} danger={hasAr} />
        </div>
        <div className="mt-3"><FinancialStatus row={row} compact /></div>
      </section>

      <div className="space-y-3 border-t border-slate-100 px-5 py-5">
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-bold text-slate-700 transition hover:border-teal-300 hover:text-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
        >
          <UserRound className="h-4 w-4" strokeWidth={2.25} />
          View patient profile
        </button>
        <label className="block">
          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">Move stage</span>
          <select
            value={row.pipeline_stage || ""}
            disabled={moving}
            onChange={(event) => onMove(event.target.value)}
            className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-semibold text-slate-700 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100 disabled:opacity-50"
          >
            {STAGES.map((stage) => <option key={stage.key} value={stage.key}>{stage.label}</option>)}
          </select>
        </label>
      </div>
    </aside>
  );
}

function MetricRow({ label, value, danger = false, stacked = false }) {
  return (
    <div className={stacked ? "min-w-0" : "flex items-center justify-between gap-3 text-[12px]"}>
      <span className={stacked ? "block text-[11px] text-slate-500" : "text-slate-500"}>{label}</span>
      <span className={[
        stacked ? "mt-1 block truncate text-[12px]" : "",
        danger ? "font-bold text-rose-700" : "font-semibold text-slate-900",
      ].join(" ")}>{value}</span>
    </div>
  );
}

function FilterPanel({ filters, options, onChange }) {
  return (
    <div data-testid="ortho-center-filters" data-tutorial-id="ortho-center-filters" className="grid gap-3 p-4 md:grid-cols-[minmax(220px,1fr)_180px_170px_170px]">
      <label className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" strokeWidth={2} />
        <input
          value={filters.search}
          onChange={(event) => onChange({ search: event.target.value })}
          placeholder="Search patient or case ID"
          className="min-h-11 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-[13px] focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
        />
      </label>
      <SelectBox testid="ortho-filter-doctor" value={filters.doctorId} onChange={(doctorId) => onChange({ doctorId })} options={[{ id: "", label: "All doctors" }, ...(options?.doctors || []).map((doctor) => ({ ...doctor, label: formatDoctorName(doctor.label || doctor.display_name || doctor.name, "Doctor") }))]} />
      <SelectBox testid="ortho-filter-status" value={filters.status} onChange={(status) => onChange({ status })} options={[{ id: "", label: "All stages" }, ...(options?.statuses || []).map((status) => ({ id: status, label: stageLabel(status) }))]} />
      <SelectBox testid="ortho-filter-payment" value={filters.payment} onChange={(payment) => onChange({ payment })} options={[{ id: "", label: "All payment states" }, ...(options?.payments || []).map((payment) => ({ id: payment, label: collectionStatusLabel(payment) }))]} />
    </div>
  );
}

function SelectBox({ testid, value, onChange, options }) {
  return (
    <label className="relative">
      <select
        data-testid={testid}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-11 w-full appearance-none rounded-lg border border-slate-300 bg-white px-3 pr-8 text-[13px] text-slate-700 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
      >
        {options.map((option) => <option key={option.id ?? option.label} value={option.id ?? ""}>{option.label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" strokeWidth={2.25} />
    </label>
  );
}

function PipelineBoard({ pipeline, expandedStages, movingCaseId, onToggleStage, onTransition, navigate }) {
  return (
    <div data-testid="ortho-pipeline-board" className="min-w-0 overflow-x-auto rounded-lg border border-slate-200 bg-white p-2">
      <div className="flex min-h-[620px] gap-2">
        {STAGES.map((stage) => {
          const bucket = pipeline?.[stage.key] || { count: 0, value_centavos: 0, contract_remaining_centavos: 0, ar_outstanding_centavos: 0, cases: [] };
          const expanded = expandedStages.has(stage.key) && !(stage.collapsedDefault && bucket.count === 0);
          const collapsed = !expanded || bucket.count === 0 && !stage.wide;
          return (
            <section
              key={stage.key}
              data-testid={`stage-${stage.key}`}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                const caseId = event.dataTransfer.getData("text/plain");
                onTransition(caseId, stage.key);
              }}
              className={[
                "shrink-0 rounded-lg border bg-slate-50/70 transition-all",
                stage.wide ? "w-[360px]" : collapsed ? "w-20" : "w-[260px]",
                stage.key === "active" ? "border-teal-200 bg-teal-50/40" : "border-slate-200",
              ].join(" ")}
            >
              <button type="button" onClick={() => onToggleStage(stage.key)} className={collapsed ? "flex h-full w-full items-center justify-center p-2" : "w-full border-b border-slate-200 px-3 py-2 text-left"}>
                {collapsed ? (
                  <div className="flex h-full flex-col items-center justify-between gap-3 py-2">
                    <span className="rounded bg-white px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-slate-700 ring-1 ring-slate-200">{bucket.count}</span>
                    <span data-stage-label className="writing-vertical text-[12px] font-semibold text-slate-700">{stage.label}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <h2 data-stage-label className="text-[15px] font-bold text-slate-950">{stage.label}</h2>
                      <span className="rounded-md bg-white px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-slate-700 ring-1 ring-slate-200">{bucket.count}</span>
                    </div>
                    <p className="mt-0.5 text-[12px] font-medium text-slate-700">
                      {formatPeso(bucket.contract_remaining_centavos)} left in plans
                      {Number(bucket.ar_outstanding_centavos || 0) > 0 ? ` · ${formatPeso(bucket.ar_outstanding_centavos)} unpaid` : ""}
                    </p>
                  </>
                )}
              </button>
              {!collapsed ? (
                <div className="space-y-2 p-2">
                  {bucket.cases.length === 0 ? (
                    <div className="rounded-md border border-dashed border-slate-200 bg-white px-3 py-8 text-center text-[12px] text-slate-500">{stage.hint}</div>
                  ) : bucket.cases.map((row) => (
                    <PatientCard key={row.ortho_case_id} row={row} moving={movingCaseId === row.ortho_case_id} navigate={navigate} />
                  ))}
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function PatientCard({ row, moving, navigate }) {
  const stage = String(row.pipeline_stage || "");
  return (
    <div
      draggable={!moving}
      onDragStart={(event) => event.dataTransfer.setData("text/plain", row.ortho_case_id)}
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/patients/${row.patient_id}?tab=ortho`)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          navigate(`/patients/${row.patient_id}?tab=ortho`);
        }
      }}
      className={["rounded-md border border-slate-200 bg-white p-3 shadow-sm transition hover:border-slate-300", moving ? "opacity-50" : ""].join(" ")}
    >
      <div className="flex items-start gap-2">
        <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" strokeWidth={2} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-bold text-slate-950">{row.patient_name || "Unnamed patient"}</div>
          <div className="mt-0.5 truncate text-[12px] text-slate-500">{cleanDoctor(row.doctor_name)}</div>
        </div>
      </div>

      {stage === "active" ? <ActiveCardBody row={row} /> : null}
      {["consultation", "records_taken", "case_proposed"].includes(stage) ? <EarlyCardBody row={row} /> : null}
      {stage === "retention" ? <RetentionCardBody row={row} /> : null}
      {stage === "completed" ? <CompletedCardBody row={row} /> : null}
      {moving ? <div className="mt-2 text-[11px] font-semibold text-teal-700">Saving stage…</div> : null}
    </div>
  );
}

function ActiveCardBody({ row }) {
  return (
    <div className="mt-3 space-y-1.5 text-[12px]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-500">Adjustments</span>
        <span className="font-semibold text-slate-900">{row.adjustments_done_count || 0} / {row.adjustments_total_count || "Not set"} adjustments</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-500">Next</span>
        <span className={row.is_overdue ? "font-semibold text-amber-700" : "font-medium text-slate-700"}>{nextDueLabel(row)}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-500">Left in plan</span>
        <span className="font-semibold text-slate-700">{formatPeso(row.contract_remaining_centavos ?? row.open_balance_centavos)}</span>
      </div>
      {Number(row.ar_outstanding_centavos || 0) > 0 ? (
        <div className="flex items-center justify-between gap-2">
          <span className="text-slate-500">Unpaid visits</span>
          <span className="font-bold text-rose-700">{formatPeso(row.ar_outstanding_centavos)}</span>
        </div>
      ) : null}
      {hasFinancialActivity(row) ? <PaymentBadge status={row.collection_status} /> : null}
    </div>
  );
}

function EarlyCardBody({ row }) {
  return (
    <div className="mt-3 space-y-1.5 text-[12px]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-500">In stage</span>
        <span className="font-medium text-slate-700">{row.case_age_days || 0}d</span>
      </div>
      <div className="rounded-md bg-slate-50 px-2 py-1.5 font-semibold text-slate-700">{row.next_action || "Review case"}</div>
    </div>
  );
}

function RetentionCardBody({ row }) {
  return (
    <div className="mt-3 space-y-1.5 text-[12px]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-500">Retainer</span>
        <span className="font-semibold text-sky-700">{titleize(row.retention_substage || "in_use")}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-slate-500">Since deband</span>
        <span className="font-medium text-slate-700">{row.days_since_deband ?? 0}d</span>
      </div>
    </div>
  );
}

function CompletedCardBody({ row }) {
  return (
    <div className="mt-3 flex items-center gap-1.5 text-[12px] font-semibold text-blue-700">
      <Archive className="h-3.5 w-3.5" strokeWidth={2.25} />
      Archived case
    </div>
  );
}

function PaymentBadge({ status }) {
  const unpaid = status === "unpaid";
  const credit = status === "credit";
  const settled = status === "settled";
  return (
    <span className={[
      "inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-semibold",
      unpaid
        ? "bg-rose-50 text-rose-700 ring-1 ring-rose-100"
        : credit
          ? "bg-sky-50 text-sky-700 ring-1 ring-sky-100"
          : settled
            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
            : "bg-teal-50 text-teal-700 ring-1 ring-teal-100",
    ].join(" ")}>
      {collectionStatusLabel(status || "current")}
    </span>
  );
}

function LoadingShell() {
  return <div className="h-[520px] animate-pulse rounded-lg border border-slate-200 bg-white" />;
}

function normalizeDashboard(data) {
  return {
    kpis: data?.kpis || {},
    pipeline: data?.pipeline || {},
    at_risk_queue: Array.isArray(data?.at_risk_queue) ? data.at_risk_queue : [],
    retainer_pipeline: Array.isArray(data?.retainer_pipeline) ? data.retainer_pipeline : [],
    filter_options: data?.filter_options || {},
    last_refreshed_at: data?.last_refreshed_at || null,
  };
}

function filterDashboardSearch(dashboard, filters) {
  if (!dashboard) return null;
  const query = String(filters.search || "").trim().toLowerCase();
  if (!query && filters.doctorId !== "__unassigned__") return dashboard;
  const next = { ...dashboard, pipeline: { ...dashboard.pipeline } };
  for (const stage of STAGES) {
    const bucket = dashboard.pipeline?.[stage.key] || { count: 0, value_centavos: 0, contract_remaining_centavos: 0, ar_outstanding_centavos: 0, cases: [] };
    const cases = (bucket.cases || []).filter((row) => {
      if (filters.doctorId === "__unassigned__" && row.doctor_id) return false;
      if (!query) return true;
      return `${row.patient_name || ""} ${row.patient_number || ""} ${row.ortho_case_id || ""}`.toLowerCase().includes(query);
    });
    next.pipeline[stage.key] = {
      ...bucket,
      cases,
      count: cases.length,
      value_centavos: cases.reduce((sum, row) => sum + Number(row.case_value_centavos || 0), 0),
      contract_remaining_centavos: cases.reduce((sum, row) => sum + Number(row.contract_remaining_centavos ?? row.open_balance_centavos ?? 0), 0),
      ar_outstanding_centavos: cases.reduce((sum, row) => sum + Number(row.ar_outstanding_centavos || 0), 0),
    };
  }
  return next;
}

function filtersFromParams(params) {
  return {
    doctorId: params.get("doctor_id") || "",
    status: params.get("status") || "",
    payment: params.get("payment") || "",
    search: params.get("q") || "",
  };
}

function countActiveFilters(filters) {
  return Object.entries(filters).filter(([key, value]) => key in EMPTY_FILTERS && Boolean(value)).length;
}

function formatPeso(value) {
  return `₱${Math.round(Number(value || 0) / 100).toLocaleString("en-PH")}`;
}

function lastUpdatedLabel(value) {
  if (!value) return "Update time not available";
  const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));
  if (minutes <= 0) return "Last updated just now";
  return `Last updated ${minutes}m ago`;
}

function nextDueLabel(row) {
  if (!row.next_adjustment_due_at) return "No due date";
  const diff = Math.ceil((new Date(row.next_adjustment_due_at).getTime() - Date.now()) / 86400000);
  if (diff < 0) return `Overdue ${Math.abs(diff)}d`;
  if (diff === 0) return "Due today";
  return `Due in ${diff}d`;
}

function lastVisitLabel(row) {
  if (row.days_since_last_adjustment == null) return "No visit saved";
  const days = Number(row.days_since_last_adjustment || 0);
  if (days === 0) return "Today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

function patientInitials(value) {
  const words = String(value || "Patient").trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((word) => word[0]?.toUpperCase()).join("") || "P";
}

function treatmentProgressLabel(row) {
  if (row.pipeline_stage === "active") {
    return `${row.adjustments_done_count || 0} of ${row.adjustments_total_count || "Not set"} visits`;
  }
  if (row.pipeline_stage === "retention") return titleize(row.retention_substage || "retention");
  if (row.pipeline_stage === "consultation") return "Records not completed";
  if (row.pipeline_stage === "records_taken") return "Records completed";
  if (row.pipeline_stage === "case_proposed") return "Ready for presentation";
  if (row.pipeline_stage === "completed") return "Treatment complete";
  return stageLabel(row.pipeline_stage);
}

function stageLabel(value) {
  return STAGES.find((stage) => stage.key === value)?.label || titleize(value);
}

function titleize(value) {
  return String(value || "").replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function collectionStatusLabel(value) {
  return {
    unpaid: "Unpaid visit",
    settled: "Fully paid",
    credit: "Credit balance",
    current: "On track",
    not_started: "No fees recorded",
  }[value] || titleize(value);
}

function cleanDoctor(value) {
  return formatDoctorName(value, "Unassigned");
}

function hasFinancialActivity(row) {
  return Number(row.case_value_centavos || 0) > 0
    || Number(row.total_paid_centavos || 0) > 0
    || Number(row.contract_remaining_centavos ?? row.open_balance_centavos ?? 0) > 0
    || Number(row.ar_outstanding_centavos || 0) > 0;
}

function getAllCases(dashboard) {
  if (!dashboard?.pipeline) return [];
  const seen = new Set();
  const rows = [];
  for (const stage of STAGES) {
    const cases = dashboard.pipeline?.[stage.key]?.cases || [];
    for (const row of cases) {
      if (!row?.ortho_case_id || seen.has(row.ortho_case_id)) continue;
      seen.add(row.ortho_case_id);
      rows.push(row);
    }
  }
  return rows;
}

function tabCounts(rows) {
  return {
    attention: buildWorkQueue(rows, "attention").length,
    records: buildWorkQueue(rows, "records").length,
    retainers: buildWorkQueue(rows, "retainers").length,
    pipeline: rows.length,
    all: rows.length,
  };
}

function buildWorkQueue(rows, tab) {
  const filtered = rows.filter((row) => {
    if (tab === "attention") return needsAttention(row);
    if (tab === "records") return row.pipeline_stage === "consultation";
    if (tab === "retainers") return row.pipeline_stage === "retention";
    if (tab === "all") return true;
    return needsAttention(row);
  });
  return [...filtered].sort((a, b) => casePriority(b) - casePriority(a) || String(a.patient_name || "").localeCompare(String(b.patient_name || "")));
}

function needsAttention(row) {
  if (row.pipeline_stage === "completed") return false;
  return Boolean(
    row.is_overdue
    || row.is_at_risk
    || row.needs_next_booking
    || Number(row.ar_outstanding_centavos || 0) > 0
    || ["consultation", "records_taken", "case_proposed", "retention"].includes(row.pipeline_stage)
  );
}

function casePriority(row) {
  let score = 0;
  if (row.is_overdue) score += 100;
  if (row.needs_next_booking) score += 80;
  if (row.is_at_risk) score += 60;
  if (Number(row.ar_outstanding_centavos || 0) > 0) score += 40;
  if (row.pipeline_stage === "case_proposed") score += 15;
  if (row.pipeline_stage === "consultation" || row.pipeline_stage === "records_taken") score += 10;
  if (row.pipeline_stage === "retention") score += 8;
  score += Math.min(Number(row.days_since_last_adjustment || row.case_age_days || 0), 30);
  return score;
}

function queueTitle(tab) {
  if (tab === "records") return "Patients who need records";
  if (tab === "retainers") return "Retainer follow-up";
  if (tab === "all") return "All ortho patients";
  return "Needs attention";
}

function queueHint(tab) {
  if (tab === "records") return "Start with records so the dentist can assess and present the case.";
  if (tab === "retainers") return "Keep debanded patients moving through impression, delivery, and use.";
  if (tab === "all") return "Every visible ortho patient from the database.";
  return "Start here. Each patient has one clear next clinic step.";
}

function caseIssue(row) {
  if (row.is_overdue) return "Overdue adjustment";
  if (row.needs_next_booking) return "No next visit";
  if (row.is_at_risk) return "Needs follow-up";
  if (row.pipeline_stage === "case_proposed") return "Ready to present";
  if (row.pipeline_stage === "records_taken") return "Present case";
  if (row.pipeline_stage === "consultation") return "Needs records";
  if (row.pipeline_stage === "retention") return "Retainer follow-up";
  if (Number(row.ar_outstanding_centavos || 0) > 0) return "Unpaid visit";
  if (row.pipeline_stage === "completed") return "Completed";
  return "On track";
}

function caseAction(row) {
  if (row.is_overdue || row.needs_next_booking) return "Book next adjustment";
  if (row.is_at_risk) return "Review follow-up";
  if (row.pipeline_stage === "case_proposed") return "Present case";
  if (row.pipeline_stage === "records_taken") return "Present plan";
  if (row.pipeline_stage === "consultation") return "Take records";
  if (row.pipeline_stage === "retention") return "Check retainer";
  if (Number(row.ar_outstanding_centavos || 0) > 0) return "Open AR";
  if (row.pipeline_stage === "completed") return "View history";
  return row.next_booked_adjustment_at ? "Next visit booked" : "Review patient";
}

function canBookAdjustment(row) {
  return !!row?.patient_id
    && !!row?.ortho_case_id
    && row.pipeline_stage === "active"
    && Boolean(row.is_overdue || row.is_at_risk || row.needs_next_booking);
}

function orthoAdjustmentDuration(row) {
  const candidates = [
    row?.adjustment_duration_minutes,
    row?.ortho_adjustment_duration_minutes,
    row?.suggested_duration_minutes,
    row?.duration_minutes,
  ];
  for (const value of candidates) {
    const minutes = Number(value);
    if (Number.isFinite(minutes) && minutes > 0) return Math.round(minutes);
  }
  return 15;
}

function orthoRequestedDays(row) {
  const candidates = [
    row?.next_adjustment_interval_days,
    row?.expected_adjustment_interval_days,
    row?.adjustment_interval_days,
  ];
  for (const value of candidates) {
    const days = Number(value);
    if (Number.isFinite(days) && days > 0) return Math.round(days);
  }
  return null;
}

function orthoLastVisitDate(row) {
  return row?.last_adjustment_at
    || row?.last_adjustment_date
    || row?.last_visit_at
    || row?.last_visit_date
    || null;
}

function orthoAdjustmentBookingBundle(row) {
  if (!row) return null;
  const minutes = orthoAdjustmentDuration(row);
  return {
    procedure_name: "Ortho Adjustment",
    category: "Orthodontics",
    categories: ["Orthodontics"],
    estimatedMinutes: minutes,
    needs: [{
      id: row.ortho_case_id,
      kind: "ortho_adjustment",
      procedure_name: "Ortho Adjustment",
      patient_id: row.patient_id,
      ortho_case_id: row.ortho_case_id,
      ortho_visit_type: "adjustment",
      duration_minutes: minutes,
    }],
  };
}

function orthoBookingSubtitle(row) {
  if (!row) return "";
  const due = nextDueLabel(row);
  const visitCount = `${row.adjustments_done_count || 0} / ${row.adjustments_total_count || "Not set"} adjustments`;
  return `${visitCount} · ${due} · ~${orthoAdjustmentDuration(row)} min`;
}

function orthoBookingNotes(row) {
  if (!row) return "";
  const parts = ["Ortho adjustment"];
  if (row.adjustments_done_count != null || row.adjustments_total_count != null) {
    parts.push(`${row.adjustments_done_count || 0}/${row.adjustments_total_count || "?"} adjustments`);
  }
  if (row.next_adjustment_due_at) parts.push(`next due ${nextDueLabel(row).toLowerCase()}`);
  return parts.join(" · ");
}

function OrthoBookingSummary({ row }) {
  return (
    <div className="rounded-md border border-teal-100 bg-teal-50 px-3 py-2 text-[12px] text-teal-900">
      <div className="font-bold text-teal-950">{row.patient_name || "Unnamed patient"}</div>
      <div className="mt-0.5">{cleanDoctor(row.doctor_name)} · {row.branch_name || "Unassigned"}</div>
      <div className="mt-1 font-semibold">{caseSubline(row)}</div>
    </div>
  );
}

function caseSubline(row) {
  if (row.pipeline_stage === "retention") return `${titleize(row.retention_substage || "retention")} · ${row.days_since_deband ?? 0}d`;
  if (row.pipeline_stage === "active") return `${row.adjustments_done_count || 0} / ${row.adjustments_total_count || "Not set"} adjustments · ${nextDueLabel(row)}`;
  return row.next_action || `${row.case_age_days || 0}d in stage`;
}

function issueTone(issue) {
  if (issue === "Overdue adjustment" || issue === "Unpaid visit") return "bg-rose-50 text-rose-700 ring-1 ring-rose-100";
  if (issue === "No next visit" || issue === "Needs follow-up") return "bg-amber-50 text-amber-800 ring-1 ring-amber-100";
  if (issue === "Retainer follow-up") return "bg-sky-50 text-sky-700 ring-1 ring-sky-100";
  if (issue === "Ready to present" || issue === "Present case") return "bg-teal-50 text-teal-700 ring-1 ring-teal-100";
  return "bg-slate-100 text-slate-700 ring-1 ring-slate-200";
}
